/**
 * Date utility functions for handling date conversions without timezone shifts.
 *
 * These helpers ensure consistent date handling across the application,
 * particularly when working with date-only values (YYYY-MM-DD) and ISO datetime strings.
 */

/**
 * Get today's date in local timezone as YYYY-MM-DD format.
 *
 * @returns {string} Date string in YYYY-MM-DD format (e.g., "2025-10-14")
 *
 * @example
 * const today = getTodayLocal(); // "2025-10-14"
 */
export const getTodayLocal = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

/**
 * Formats a date string for HTML date input (YYYY-MM-DD format).
 * Handles various input formats and avoids timezone issues.
 *
 * @param {string | Date | undefined} date - Date string or Date object
 * @returns {string} Formatted date string in YYYY-MM-DD format or empty string
 *
 * @example
 * formatDateForInput("2025-10-14T00:00:00.000Z"); // "2025-10-14"
 * formatDateForInput(new Date()); // "2025-10-14"
 * formatDateForInput(""); // ""
 */
export const formatDateForInput = (date: string | Date | undefined): string => {
  if (!date) return "";
  const d = typeof date === "string" ? new Date(date) : date;

  // Check if date is valid
  if (isNaN(d.getTime())) return "";

  // Extract date components to avoid timezone issues
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

/**
 * Convert a date-only string to ISO datetime format without timezone shift.
 *
 * This ensures that a date like "2025-10-14" becomes "2025-10-14T00:00:00.000Z"
 * instead of shifting to the previous or next day due to timezone conversion.
 *
 * @param {string | Date | null | undefined} dateInput - Date string in YYYY-MM-DD format, Date object, or ISO format
 * @returns {string | null} ISO datetime string or null if input is invalid/empty
 *
 * @example
 * dateToISOString("2025-10-14"); // "2025-10-14T00:00:00.000Z"
 * dateToISOString(new Date("2025-10-14")); // "2025-10-14T05:00:00.000Z" (depends on timezone)
 * dateToISOString(""); // null
 * dateToISOString(null); // null
 */
export const dateToISOString = (
  dateInput: string | Date | null | undefined
): string | null => {
  if (!dateInput) return null;

  // If it's already a Date object, just convert it
  if (dateInput instanceof Date) {
    return dateInput.toISOString();
  }

  // If it's a date-only string (YYYY-MM-DD), append time to avoid timezone shifts
  const dateStr = dateInput.toString().trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    // Date-only format: treat as midnight UTC to preserve the date
    return `${dateStr}T00:00:00.000Z`;
  }

  // Otherwise, parse as a regular date
  return new Date(dateInput).toISOString();
};

/**
 * Extract the date-only portion (YYYY-MM-DD) from an ISO datetime string.
 *
 * This safely extracts the date without timezone conversion, ensuring
 * "2025-10-14T00:00:00.000Z" always becomes "2025-10-14" regardless of local timezone.
 *
 * @param {string | null | undefined} isoString - ISO datetime string from API
 * @returns {string} Date string in YYYY-MM-DD format or empty string
 *
 * @example
 * isoToDateOnly("2025-10-14T00:00:00.000Z"); // "2025-10-14"
 * isoToDateOnly("2025-10-14"); // "2025-10-14"
 * isoToDateOnly(null); // ""
 */
export const isoToDateOnly = (isoString: string | null | undefined): string => {
  if (!isoString) return "";

  // Extract just the date part (YYYY-MM-DD) from ISO string
  // This avoids timezone shifts when displaying in date inputs
  const match = isoString.match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : "";
};

/**
 * Format a date string for display in MM/DD/YYYY format.
 *
 * - Date-only strings (YYYY-MM-DD): Displayed as-is (no timezone conversion)
 * - Midnight UTC datetime strings (T00:00:00): Treated as date-only values
 * - ISO datetime strings with actual times: Converted to local timezone for display
 *
 * @param {string | null | undefined} dateStr - Date string in YYYY-MM-DD or ISO format
 * @returns {string} Date formatted as MM/DD/YYYY or empty string
 */
export const formatDateForDisplay = (
  dateStr: string | null | undefined
): string => {
  if (!dateStr) return "";

  const str = dateStr.toString();

  // Date-only format (YYYY-MM-DD): parse directly without timezone conversion
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
    const [year, month, day] = str.split("-");
    return `${month}/${day}/${year}`;
  }

  // Midnight UTC datetime (e.g., "2025-12-01T00:00:00" or "2025-12-01T00:00:00Z")
  // These are date-only values stored as datetime, extract the date portion directly
  if (/^\d{4}-\d{2}-\d{2}T00:00:00/.test(str)) {
    const [year, month, day] = str.substring(0, 10).split("-");
    return `${month}/${day}/${year}`;
  }

  const date = new Date(str);
  if (isNaN(date.getTime())) return "";

  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
};
